import { useState, useEffect, useRef } from 'react'

import _ from 'lodash'
import ReactPlayer from 'react-player'

import { Button } from '@/shared/ui/Button'

import { useLevelPlaySurface } from '../surface'

/** Fração assistida que já conta como fim — o polling pode pular o último quadro. */
const END_FRACTION = 0.98
/** Distância do fim, em segundos, a partir da qual voltar ao início é loop, não seek. */
const END_WINDOW_SECONDS = 2

/**
 * Passo de vídeo. O footer é injetado por `setStepsCustomFooters` — o kind
 * controla o próprio CTA, porque a liberação depende de o vídeo ter terminado
 * (ou de um tempo mínimo, quando `next_button_enabled`).
 *
 * A conclusão vai por `onContinue`, que **persiste** o passo no backend antes de
 * avançar. A versão do quiz chamava `onComplete` — que só marca o estado local
 * e, pior, recebia `{ uid }` onde a assinatura espera `{ answer }`, deixando a
 * resposta `undefined`. Resultado: vídeo assistido no quiz não era registrado.
 */
export const LevelPlayVideo = ({ step, activity }) => {
  const { onContinue, isSubmitting, setStepsCustomFooters } =
    useLevelPlaySurface()
  const [canContinue, setCanContinue] = useState(false)
  const { meta } = activity
  const videoUrl = _.get(meta, 'video_url')
  const nextButtonEnabled = _.get(meta, 'next_button_enabled', false)
  const advanceEnabled = _.get(meta, 'advance_enabled', false)

  // O motor da jornada recria `onContinue` a cada render (`steps` é derivado de
  // novo em `useLevel`), e reinjetar o footer re-renderiza o motor. Com
  // `onContinue` nas dependências do efeito abaixo, a etapa entrava em laço
  // (`Maximum update depth exceeded`) enquanto estivesse montada. O clique lê a
  // versão mais recente por ref; o efeito só reage ao que o footer mostra.
  const onContinueRef = useRef(onContinue)
  onContinueRef.current = onContinue

  // O fim não pode depender só de `onEnded`. No celular, o embed do YouTube
  // toca Short em loop nativo (`<video loop>`): o tempo volta ao zero e o
  // ENDED nunca é emitido — o "Continuar" não liberava e o participante via o
  // vídeo recomeçar sem fim. O progresso (polling de 1 s do `react-player`)
  // cobre esse caso: chegar perto do fim e voltar ao início é fim assistido.
  const durationRef = useRef(0)
  const lastSecondsRef = useRef(0)

  const handleProgress = ({ played, playedSeconds }) => {
    const duration = durationRef.current
    const lastSeconds = lastSecondsRef.current
    lastSecondsRef.current = playedSeconds

    const reachedEnd = played >= END_FRACTION
    const wrappedFromEnd =
      Number.isFinite(duration) &&
      duration > 0 &&
      lastSeconds >= duration - END_WINDOW_SECONDS &&
      playedSeconds < lastSeconds - 1

    if (reachedEnd || wrappedFromEnd) setCanContinue(true)
  }

  useEffect(() => {
    if (nextButtonEnabled) {
      setTimeout(() => {
        setCanContinue(true)
      }, 3000)
    }
  }, [nextButtonEnabled])

  useEffect(() => {
    setStepsCustomFooters(
      step.id,
      canContinue ? (
        <Button
          variant="primary"
          className="grow mt-auto"
          showTrailingIcon={false}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={() => onContinueRef.current({ uid: activity.uid })}
        >
          {isSubmitting ? 'Carregando' : 'Continuar'}
        </Button>
      ) : (
        <Button
          variant="primary"
          className="grow mt-auto"
          showTrailingIcon={false}
          disabled
        >
          {nextButtonEnabled ? 'Continuar' : 'Assista ao vídeo'}
        </Button>
      )
    )
  }, [
    canContinue,
    isSubmitting,
    nextButtonEnabled,
    setStepsCustomFooters,
    step.id,
    activity.uid
  ])

  return (
    // `overflow-hidden` recorta o player pelo raio do contêiner — técnica do
    // quiz, mais robusta que estilizar o `iframe` por seletor descendente.
    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
      <ReactPlayer
        url={videoUrl}
        controls={advanceEnabled}
        width="100%"
        height="100%"
        loop={false}
        // Sem isto o vídeo abre em tela cheia no iOS, tirando o participante do
        // fluxo do passo. Estava só na jornada.
        playsinline
        onEnded={() => setCanContinue(true)}
        onDuration={duration => {
          durationRef.current = duration
        }}
        onProgress={handleProgress}
        config={{
          youtube: {
            playerVars: {
              autoplay: 0,
              rel: 0,
              showinfo: 0,
              modestbranding: 1,
              disablekb: 1,
              iv_load_policy: 3,
              playsinline: 1
            }
          }
        }}
      />
    </div>
  )
}
